import { useState, useEffect } from 'react';
import { globalCampaignAPI, quarantineAPI, reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Loader2, Shield, RefreshCw, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function DomainPerformancePage() {
  const [domains, setDomains] = useState([]);
  const [report, setReport] = useState(null);
  const [quarantined, setQuarantined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const [dRes, rRes, qRes] = await Promise.all([
        globalCampaignAPI.domainAudit(),
        reportsAPI.deliverability(),
        quarantineAPI.list(),
      ]);
      setDomains(dRes.data);
      setReport(rRes.data);
      setQuarantined(qRes.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleQuarantineCheck = async () => {
    const res = await quarantineAPI.check();
    toast.success(`${res.data.quarantined} domain karantinaya alindi`);
    load();
  };

  const handleRelease = async (id) => {
    await quarantineAPI.release(id, 'Admin tarafindan serbest birakildi');
    toast.success('Domain serbest birakildi');
    load();
  };

  const filtered = domains.filter(d => {
    if (filter === 'all') return true;
    if (filter === 'quarantined') return d.quarantine_status === 'quarantined';
    return d.health_status === filter;
  });

  const statusIcon = (s) => s === 'elite' || s === 'healthy' ? <CheckCircle className="w-4 h-4 text-green-500" /> : s === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <XCircle className="w-4 h-4 text-red-500" />;
  const statusColor = (s) => s === 'elite' ? 'text-emerald-400' : s === 'healthy' ? 'text-green-500' : s === 'warning' ? 'text-amber-500' : 'text-red-500';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Domain Performansi</h1><p className="text-muted-foreground mt-1">{domains.length} domain izleniyor</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleQuarantineCheck}><Shield className="w-4 h-4 mr-1" /> Karantina Tara</Button>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
        </div>
      </div>

      {/* Deliverability Summary */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Toplam Domain', value: report.total_domains },
            { label: 'Toplam Gonderim', value: report.total_sent },
            { label: 'Delivery Rate', value: `%${report.delivery_rate}`, color: 'text-green-500' },
            { label: 'Open Rate', value: `%${report.open_rate}`, color: 'text-blue-400' },
            { label: 'Bounce Rate', value: `%${report.bounce_rate}`, color: report.bounce_rate > 3 ? 'text-red-400' : 'text-muted-foreground' },
            { label: 'Risk', value: report.risk_level.toUpperCase(), color: report.risk_level === 'excellent' ? 'text-emerald-400' : report.risk_level === 'good' ? 'text-green-500' : 'text-amber-500' },
          ].map((s, i) => (
            <div key={i} className="rounded-lg bg-card p-4 text-center">
              <p className={`text-2xl font-bold ${s.color || ''}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-1">
        {[{k:'all',l:'Tumu'},{k:'elite',l:'Elite'},{k:'healthy',l:'Healthy'},{k:'warning',l:'Warning'},{k:'critical',l:'Critical'},{k:'quarantined',l:'Karantina'}].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1 rounded text-xs font-medium ${filter===f.k?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground'}`}>{f.l}</button>
        ))}
      </div>

      {/* Domain Table */}
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left py-2 px-2">Domain</th>
                <th className="text-center px-2">Score</th>
                <th className="text-center px-2">Day</th>
                <th className="text-center px-2">Limit</th>
                <th className="text-center px-2">Bugun</th>
                <th className="text-center px-2">Toplam</th>
                <th className="text-center px-2">Open%</th>
                <th className="text-center px-2">Bounce%</th>
                <th className="text-center px-2">Spam%</th>
                <th className="text-center px-2">Durum</th>
              </tr></thead>
              <tbody>
                {filtered.map((d, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="py-2 px-2 font-medium">{d.domain}</td>
                    <td className={`text-center px-2 font-bold ${statusColor(d.health_status)}`}>{d.health_score}</td>
                    <td className="text-center px-2">{d.warmup_day}</td>
                    <td className="text-center px-2">{d.daily_limit}</td>
                    <td className="text-center px-2">{d.sent_today}</td>
                    <td className="text-center px-2">{d.total_sent}</td>
                    <td className="text-center px-2">{d.total_opens > 0 ? `${(d.total_opens/Math.max(d.total_sent,1)*100).toFixed(0)}%` : '-'}</td>
                    <td className="text-center px-2">{d.total_bounces > 0 ? `${(d.total_bounces/Math.max(d.total_sent,1)*100).toFixed(1)}%` : '-'}</td>
                    <td className="text-center px-2">{d.total_spam > 0 ? `${(d.total_spam/Math.max(d.total_sent,1)*100).toFixed(2)}%` : '-'}</td>
                    <td className="text-center px-2">
                      <div className="flex items-center justify-center gap-1">
                        {statusIcon(d.health_status)}
                        {d.quarantine_status === 'quarantined' && (
                          <Button variant="ghost" size="sm" className="text-xs h-6 px-1" onClick={() => handleRelease(d.domain)}>Serbest</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quarantined Domains */}
      {quarantined.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base text-red-400">Karantina Listesi</CardTitle></CardHeader>
          <CardContent>
            {quarantined.map((d, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border/50">
                <div><p className="font-medium">{d.domain}</p><p className="text-xs text-red-400">{d.quarantine_reason}</p></div>
                <Button variant="outline" size="sm" onClick={() => handleRelease(d.id)}>Serbest Birak</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
